import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Card,
  ChipToggle,
  DataTable,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  Select,
  StatTile,
  useToast,
  type Column,
} from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import {
  useBillingSummary,
  useBills,
  useCreateBill,
  useFeeServices,
} from '@/features/billing/api';
import { formatRM } from '@/features/billing/money';
import type { Bill } from '@/features/billing/types';
import { useBranch } from '@/features/branch/branch-context';
import { usePayers } from '@/features/patients/api';
import type { PatientSummary } from '@/features/patients/types';
import { PageContainer } from '@/layouts/PageContainer';
import { isApiError } from '@/lib/api';
import { useOptionalUrlState, useUrlState } from '@/lib/url-state';
import dash from '@/pages/DashboardPage.module.css';
import { PatientPicker } from '@/pages/queue/PatientPicker';
import styles from '@/pages/queue/queue.module.css';
import { BillDrawer } from './BillDrawer';
import { BillStatusBadge } from './BillStatusBadge';
import {
  PAID_BY_OPTIONS,
  billMatchesPaidBy,
  billPaymentSummary,
  type PaidByFilter,
} from './payment-summary';

type Filter = 'today' | 'open' | 'closed' | 'void' | 'all';
const FILTERS: { value: Filter; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'open', label: 'Open' },
  { value: 'closed', label: 'Paid' },
  { value: 'void', label: 'Void' },
  { value: 'all', label: 'All' },
];

/**
 * Admin v2 Front desk "Billing" (artifact #28): open bills, collected today,
 * the amount to claim from panels and claims created; today's bills with the
 * panel's share and the patient's share. New bill, View, Close bill.
 */
export function BillingPage() {
  const { t } = useTranslation();
  const { selected: branch, canSelectAll } = useBranch();
  const { hasPermission } = useSession();
  const [filter, setFilter] = useUrlState<Filter>('filter', 'today', [
    'today',
    'open',
    'closed',
    'void',
    'all',
  ]);
  const [open, setOpen] = useOptionalUrlState('drawer');
  const [paidBy, setPaidBy] = useState<PaidByFilter>('all');
  const creating = open === 'new' && !!branch && hasPermission('bills:manage');
  const { data, isLoading, error, refetch } = useBills(filter);
  const {
    data: summaryData,
    error: summaryError,
    refetch: refetchSummary,
  } = useBillingSummary();
  const summary = summaryError ? undefined : summaryData;
  const scope = branch?.name ?? t('shell.allBranchesOption');

  if (!branch && !canSelectAll) {
    return (
      <PageContainer>
        <p className={styles.warnNote}>{t('billing.chooseBranch')}</p>
      </PageContainer>
    );
  }

  const columns: Column<Bill>[] = [
    {
      key: 'no',
      header: 'Bill',
      render: (b) => <span className="mono">{b.number}</span>,
    },
    ...(!branch
      ? [
          {
            key: 'branch',
            header: t('shell.branch'),
            render: (b: Bill) => b.branch.name,
          },
        ]
      : []),
    {
      key: 'patient',
      header: 'Patient',
      render: (b) => (
        <>
          <p className={styles.name}>{b.patient.fullName}</p>
          <p className={styles.sub}>
            {b.payer ? b.payer.name : 'Self-pay'}
            {b.encounter ? ` · ${b.encounter.doctor.fullName}` : ''}
          </p>
        </>
      ),
    },
    {
      key: 'lines',
      header: 'Items',
      align: 'right',
      render: (b) => b.lines.length,
    },
    {
      key: 'total',
      header: 'Total',
      align: 'right',
      render: (b) => formatRM(b.grossSen),
    },
    {
      key: 'panel',
      header: 'Panel pays',
      align: 'right',
      render: (b) => (b.panelSen ? formatRM(b.panelSen) : '—'),
    },
    {
      key: 'patientPays',
      header: 'Patient pays',
      align: 'right',
      render: (b) => <strong>{formatRM(b.totalSen)}</strong>,
    },
    {
      key: 'paidBy',
      header: 'Paid by',
      render: (b) => billPaymentSummary(b),
    },
    {
      key: 'status',
      header: 'Status',
      render: (b) => (
        <div className={styles.badges}>
          <BillStatusBadge bill={b} />
          {b.status === 'open' && b.unpricedLines > 0 && (
            <Badge tone="warning">{`${b.unpricedLines} need a price`}</Badge>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      header: <span className="visually-hidden">Actions</span>,
      align: 'right',
      render: (b) => (
        <Button
          size="sm"
          variant={
            branch?.id === b.branch.id &&
            b.status === 'open' &&
            hasPermission('payments:record')
              ? 'primary'
              : undefined
          }
          onClick={() => setOpen(b.id)}
        >
          {branch?.id === b.branch.id &&
          b.status === 'open' &&
          hasPermission('payments:record')
            ? 'Close bill'
            : 'View'}
        </Button>
      ),
    },
  ];

  return (
    <PageContainer>
      <p className={styles.sub}>{t('billing.summaryScope', { scope })}</p>
      <div className={dash.tiles}>
        <StatTile
          label="Open bills"
          value={summary?.openBills ?? '—'}
          caption={
            summary
              ? `${formatRM(summary.openBalanceSen)} to collect`
              : undefined
          }
          highlight
        />
        <StatTile
          label="Collected today"
          value={summary ? formatRM(summary.collectedTodayTotalSen) : '—'}
          caption={
            summary
              ? `Cash ${formatRM(summary.collectedTodaySen.cash)} · card ${formatRM(summary.collectedTodaySen.card)} · e-wallet ${formatRM(summary.collectedTodaySen.e_wallet)} · DuitNow ${formatRM(summary.collectedTodaySen.duitnow_qr)}`
              : undefined
          }
        />
        <StatTile
          label="To claim from panels"
          value={summary ? formatRM(summary.toClaimFromPanelsSen) : '—'}
          caption="Panel share of today's bills"
        />
        <StatTile
          label="Claims created here"
          value={summary?.claimsCreatedToday ?? '—'}
          caption={
            summary
              ? `${formatRM(summary.claimsCreatedTodaySen)} as draft claims for Finance`
              : undefined
          }
        />
      </div>
      {summaryError && (
        <ErrorState
          error={summaryError}
          title={t('billing.summaryFailed')}
          onRetry={() => void refetchSummary()}
        />
      )}
      <Card
        title={`${filter === 'today' ? "Today's bills" : 'Bills'} · ${scope}`}
        flush
        actions={
          <div className={styles.badges}>
            <ChipToggle
              label="Show"
              single
              options={FILTERS}
              selected={[filter]}
              onChange={(v) => v[0] && setFilter(v[0])}
            />
            <Select
              aria-label="Paid by"
              value={paidBy}
              onChange={(e) => setPaidBy(e.target.value as PaidByFilter)}
              options={PAID_BY_OPTIONS}
            />
            {branch && hasPermission('bills:manage') && (
              <Button
                size="sm"
                variant="primary"
                icon={<Plus size={14} />}
                onClick={() => setOpen('new')}
              >
                New bill
              </Button>
            )}
          </div>
        }
      >
        {!branch && hasPermission('bills:manage') && (
          <p className={styles.warnNote}>{t('billing.chooseBranchToCreate')}</p>
        )}
        <DataTable
          caption="Bills"
          columns={columns}
          rows={data?.filter((b) => billMatchesPaidBy(b, paidBy))}
          rowKey={(b) => b.id}
          loading={isLoading}
          error={error}
          onRetry={() => void refetch()}
          empty={
            <EmptyState
              title="No bills here"
              body="A bill is built when the doctor closes the encounter, or with New bill."
            />
          }
        />
      </Card>
      <BillDrawer
        billId={open === 'new' ? null : open}
        onClose={() => setOpen(null)}
      />
      {creating && (
        <NewBillDialog
          key={branch?.id}
          onClose={() => setOpen(null)}
          onCreated={(id) => {
            setOpen(id);
          }}
        />
      )}
    </PageContainer>
  );
}

/** "New bill" (artifact #29): patient, payer, first service, member price. */
function NewBillDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const { selected: branch } = useBranch();
  const toast = useToast();
  const create = useCreateBill();
  const { data: payers } = usePayers();
  const { data: services } = useFeeServices();
  const [patient, setPatient] = useState<PatientSummary | null>(null);
  const [payerId, setPayerId] = useState('');
  const [serviceId, setServiceId] = useState('');
  const [member, setMember] = useState(false);
  const here = (services ?? []).filter(
    (s) => s.allBranches || s.branches.some((b) => b.id === branch?.id),
  );
  return (
    <Dialog
      open
      title="New bill"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!patient || !serviceId}
            loading={create.isPending}
            onClick={() =>
              patient &&
              create
                .mutateAsync({
                  patientId: patient.id,
                  payerId: payerId || null,
                  serviceId,
                  memberPrice: member,
                })
                .then((b) => onCreated(b.id))
                .catch((err: unknown) =>
                  toast.show({
                    tone: 'error',
                    title: 'Not done',
                    body: isApiError(err) ? err.message : undefined,
                    error: err,
                  }),
                )
            }
          >
            Create bill
          </Button>
        </>
      }
    >
      <div className={styles.drawer}>
        <Field label="Patient">
          <PatientPicker value={patient} onChange={setPatient} />
        </Field>
        <Field label="Payer">
          <Select
            value={payerId}
            onChange={(ev) => setPayerId(ev.target.value)}
            options={[
              { value: '', label: 'Self-pay' },
              ...(payers ?? []).map((p) => ({ value: p.id, label: p.name })),
            ]}
          />
        </Field>
        <Field label="Service">
          <Select
            value={serviceId}
            onChange={(ev) => setServiceId(ev.target.value)}
            options={[
              { value: '', label: 'Choose…' },
              ...here.map((s) => ({
                value: s.id,
                label: `${s.name}${s.priceSen !== null ? ` · ${formatRM(s.priceSen)}` : ' · price not set'}`,
              })),
            ]}
          />
        </Field>
        <ChipToggle
          label="Member price applies"
          single
          options={[
            { value: 'no', label: 'No' },
            { value: 'yes', label: 'Yes' },
          ]}
          selected={[member ? 'yes' : 'no']}
          onChange={(v) => v[0] && setMember(v[0] === 'yes')}
        />
      </div>
    </Dialog>
  );
}
